/**
 * Terminal Page
 *
 * Web-based terminal interface for LUKHAS
 * Supports local, sandbox, and bastion execution modes
 */

import React, { useState } from 'react'
import { Terminal as TerminalIcon, Server, Shield, Home, Info } from 'lucide-react'
import { XTerminal } from '../components/terminal/XTerminal'

type TerminalMode = 'local' | 'sandbox' | 'bastion'

export const TerminalPage: React.FC = () => {
  const [mode, setMode] = useState<TerminalMode>('local')
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showInfo, setShowInfo] = useState(false)

  const handleSessionCreated = (id: string) => {
    setSessionId(id)
    setError(null)
  }

  const handleError = (errorMsg: string) => {
    setError(errorMsg)
  }

  const handleModeChange = (newMode: TerminalMode) => {
    if (sessionId) {
      const confirmChange = window.confirm(
        'Changing mode will close the current session. Continue?'
      )
      if (!confirmChange) return
    }
    setMode(newMode)
    setSessionId(null)
    setError(null)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-lg">
                <TerminalIcon className="w-8 h-8 text-cyan-400" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">
                  LUKHAS Terminal
                </h1>
                <p className="text-gray-400 mt-1">
                  Λ Consciousness-Aware Command Interface
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowInfo(!showInfo)}
              className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
            >
              <Info className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Info Panel */}
          {showInfo && (
            <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-400 mb-2">About LUKHAS Terminal</h3>
              <div className="text-gray-300 space-y-2 text-sm">
                <p>
                  <strong>Local Mode:</strong> Execute commands directly on the server (use with caution)
                </p>
                <p>
                  <strong>Sandbox Mode:</strong> Execute commands in isolated Docker containers
                </p>
                <p>
                  <strong>Bastion Mode:</strong> Connect to remote servers via secure bastion hosts
                </p>
                <p className="text-gray-400 mt-3">
                  All sessions are logged and audited via NIAS for security and compliance.
                </p>
              </div>
            </div>
          )}

          {/* Mode Selector */}
          <div className="mt-6 flex items-center space-x-4">
            <span className="text-gray-400 text-sm font-medium">Execution Mode:</span>
            <div className="flex space-x-2">
              <button
                onClick={() => handleModeChange('local')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                  mode === 'local'
                    ? 'bg-blue-500/20 border border-blue-500/50 text-blue-400'
                    : 'bg-gray-800/50 border border-gray-700 text-gray-400 hover:bg-gray-800'
                }`}
              >
                <Home className="w-4 h-4" />
                <span className="font-medium">Local</span>
              </button>

              <button
                onClick={() => handleModeChange('sandbox')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                  mode === 'sandbox'
                    ? 'bg-green-500/20 border border-green-500/50 text-green-400'
                    : 'bg-gray-800/50 border border-gray-700 text-gray-400 hover:bg-gray-800'
                }`}
              >
                <Shield className="w-4 h-4" />
                <span className="font-medium">Sandbox</span>
              </button>

              <button
                onClick={() => handleModeChange('bastion')}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
                  mode === 'bastion'
                    ? 'bg-purple-500/20 border border-purple-500/50 text-purple-400'
                    : 'bg-gray-800/50 border border-gray-700 text-gray-400 hover:bg-gray-800'
                }`}
              >
                <Server className="w-4 h-4" />
                <span className="font-medium">Bastion</span>
              </button>
            </div>

            {sessionId && (
              <div className="ml-auto flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-gray-400 text-sm">
                  Session: <span className="text-gray-300 font-mono">{sessionId.slice(0, 8)}</span>
                </span>
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-red-400 text-sm">
                <strong>Error:</strong> {error}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Terminal Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-lg overflow-hidden shadow-2xl">
          <div className="p-1 bg-gradient-to-r from-gray-800 to-gray-900 border-b border-gray-700">
            <div className="flex items-center space-x-2 px-3 py-2">
              <div className="w-3 h-3 rounded-full bg-red-500/80" />
              <div className="w-3 h-3 rounded-full bg-yellow-500/80" />
              <div className="w-3 h-3 rounded-full bg-green-500/80" />
              <span className="ml-4 text-xs text-gray-500 font-mono">
                lukhas-terminal-{mode}
              </span>
            </div>
          </div>

          <div className="bg-[#1a1b26] p-4" style={{ height: 'calc(100vh - 400px)', minHeight: '500px' }}>
            <XTerminal
              key={mode}
              mode={mode}
              onSessionCreated={handleSessionCreated}
              onError={handleError}
              theme={{
                background: '#1a1b26',
                foreground: '#c0caf5',
                cursor: '#c0caf5',
              }}
            />
          </div>
        </div>

        {/* Quick Commands */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-300 mb-2">Quick Commands</h3>
            <div className="space-y-1 text-xs font-mono text-gray-400">
              <div><span className="text-cyan-400">ls -la</span> - List files</div>
              <div><span className="text-cyan-400">pwd</span> - Current directory</div>
              <div><span className="text-cyan-400">echo $PATH</span> - View PATH</div>
            </div>
          </div>

          <div className="p-4 bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-300 mb-2">Keyboard Shortcuts</h3>
            <div className="space-y-1 text-xs text-gray-400">
              <div><kbd className="px-1 bg-gray-800 rounded">↑/↓</kbd> History navigation</div>
              <div><kbd className="px-1 bg-gray-800 rounded">Ctrl+C</kbd> Cancel command</div>
              <div><kbd className="px-1 bg-gray-800 rounded">Ctrl+L</kbd> Clear screen</div>
            </div>
          </div>

          <div className="p-4 bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-lg">
            <h3 className="text-sm font-semibold text-gray-300 mb-2">Session Info</h3>
            <div className="space-y-1 text-xs text-gray-400">
              <div>Mode: <span className="text-gray-300">{mode}</span></div>
              <div>Status: <span className="text-green-400">{sessionId ? 'Active' : 'Initializing'}</span></div>
              <div>Audit: <span className="text-blue-400">NIAS Enabled</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TerminalPage
